import React, { useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import { Table as MuiTable, TableRow as MuiTableRow } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import FilterListIcon from "@mui/icons-material/FilterList";
import { TableWrapper } from "./Table.styled";
import xor from "lodash/xor";
import reject from "lodash/reject";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Skeleton from "@mui/material/Skeleton";
import { useTranslation } from "react-i18next";
import { TableRow } from "./index";

export type TableProps<T> = {
  data: T[];
  tableName?: string;
  pagination?: {
    total: number;
    current: number;
    pageSize: number;
  };
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (page: number) => void;
  columns: Array<{
    title: string | React.ReactNode;
    render: (row: T, index: number) => React.ReactNode;
    thRender?: () => React.ReactNode;
    key: string;
    noWrap?: boolean;
    hide?: boolean;
    children?: (row: T, index: number) => React.ReactNode;
  }>;
  rowKey?: keyof T;
  className?: string;
  isOpenable?: boolean;
  isSelectable?: boolean;
  isLoading?: boolean;
  groupActions?: React.ReactNode;
  filters?: React.ReactNode;
  noDataText?: string;
  maxHeight?: number;
  rowDetails?: (row: T, index: number) => React.ReactNode;
};

const Table = <T extends object>({
   data,
   tableName,
   columns,
   rowKey,
   pagination,
   onChangePage = () => {},
   onChangeRowsPerPage = () => {},
   isOpenable,
   isSelectable,
   isLoading = false,
   filters,
   noDataText,
   ...props
}: TableProps<T>) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<Array<string | number>>([]);
    const [isFiltersShowing, setIsFiltersShowing] = useState(false);

    const getRowKey = (index: number) => {
        return rowKey ? String(data[index][rowKey]) : index;
    };

    const handleSelectRow = (id: string | number) => {
        setSelected((prev) => xor(prev, [id]));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const selected = data.map((_, index) => getRowKey(index));
            setSelected(selected);
            return;
        }
        setSelected([]);
    };

    const filteredColumns = reject(columns, (column) => !!column.hide);

    return (
        <TableWrapper>
            <Box
                pt={1}
                pb={1}
                pl={0}
                pr={0}
                display="flex"
                justifyContent="space-between"
                // alignItems="center"
                bgcolor={selected.length > 0 ? "primary.light" : undefined}
                color={selected.length > 0 ? "primary.main" : undefined}
                // minHeight={filters || (tableName && 48)}
                minHeight={(tableName && 48)}
            >
                {selected.length > 0 && (
                    <Typography variant="subtitle1">{selected.length} {t('selected-rows')}</Typography>
                )}
                {selected.length === 0 && (
                    <>
                        <Typography variant="subtitle1">{tableName}</Typography>
                        {filters && (
                            <IconButton onClick={() => setIsFiltersShowing((prevState) => !prevState)}>
                                <FilterListIcon/>
                            </IconButton>
                        )}
                    </>
                )}
            </Box>
            {filters && isFiltersShowing && (
                <Box pl={2} pr={2} mb={4} mt={1}>
                    {filters}
                </Box>
            )}
            <TableContainer sx={{ maxHeight: props?.maxHeight || 460 }}>
                <MuiTable stickyHeader size="medium" padding="none">
                    <TableHead>
                        <MuiTableRow>
                            {isOpenable && <TableCell></TableCell>}
                            {isSelectable && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < data.length}
                                        checked={data.length > 0 && selected.length === data.length}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                            )}
                            {filteredColumns?.map((column, columnIndex) => (
                                <TableCell
                                    key={column.key || columnIndex}
                                    style={{ paddingBottom: 10 }}
                                >
                                    {column.thRender ? column.thRender() : column.title}
                                </TableCell>
                            ))}
                        </MuiTableRow>
                    </TableHead>
                    <TableBody>
                        {(data?.length === 0 || isLoading) && (
                            <MuiTableRow tabIndex={-1}>
                                <TableCell
                                    colSpan={isSelectable ? filteredColumns?.length + 1 : filteredColumns?.length}
                                    align="center"
                                >
                                    {isLoading ? (
                                        <>
                                            {new Array(
                                                pagination?.pageSize || 3
                                            ).fill(null).map((_, index) => {
                                                return <Skeleton key={index} style={{ padding: "10px", margin: "5px" }} animation="wave"/>
                                            })}
                                        </>
                                    ) : (
                                        <Box>{ noDataText ?? t('no-data') }</Box>
                                    )}
                                </TableCell>
                            </MuiTableRow>
                        )}
                        {(!isLoading && data?.length > 0) && data?.map((row, rowIndex) => {
                          // const rowKey = getRowKey(rowIndex);
                          // const isItemOpen = opened.includes(rowKey);
                          // const isItemSelected = selected.includes(rowKey);

                          return <TableRow
                            key={rowIndex}
                            data={row}
                            dataIndex={rowIndex}
                            columns={filteredColumns}
                            isOpenable={isOpenable}
                            isSelectable={isSelectable}
                            { ...props }
                          ></TableRow>
                        })}
                    </TableBody>
                </MuiTable>
            </TableContainer>
            {pagination && (
              <TablePagination
                // rowsPerPageOptions={[10, 30, 50, 100]}
                rowsPerPageOptions={[]}
                rowsPerPage={pagination.pageSize ?? 0}
                component='div'
                count={pagination.total ?? 0}
                page={pagination.current - 1}
                onPageChange={(_, page) => onChangePage(page + 1)}
                // onRowsPerPageChange={(_, page) => onChangeRowsPerPage()}
              />
            )}
        </TableWrapper>
    );
};

export default Table;
